import { useQueryClient } from '@tanstack/react-query';
import { useEcho } from '@laravel/echo-react';
import { Plus } from 'lucide-react';
import { Options } from '@/components/inspector/entity-fields/form/options';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { ShareOptionItem } from '@/components/shares/share-option-item';
import { useNodeSharesList } from '@/hooks/shares/useNodeShares';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { type NodeType, type TheNode } from '@/types/tree';
import { QueryKey } from '@/lib/queryKeys';
import { type Asset } from '@/types/asset';
import { useTranslations } from 'next-intl';

const ShareListener = ({ nodeId }: { nodeId: string }) => {
  const queryClient = useQueryClient();

  useEcho(
    `node.${nodeId}.shares`,
    '.share.usersInvited',
    (e: { nodeId: string; shareId: string }) => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.NodeShareList, e.nodeId],
        refetchType: 'all',
      });
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.GetShare, e.nodeId, e.shareId],
        refetchType: 'all',
      });
    },
    [queryClient],
  );

  return null;
};

export const SharesAccordion = ({
  disabled,
  item,
  currentSelectedType,
  asset,
  readOnly,
  enabled,
}: {
  disabled: boolean;
  item: TheNode;
  currentSelectedType: NodeType;
  asset: Asset;
  readOnly: boolean;
  enabled: boolean;
}) => {
  const tShares = useTranslations('shares');
  const openModal = useOpenModal();

  const { shareList, shareListIsPending } = useNodeSharesList(
    {
      nodeId: item?.nodeId,
      pagination: {
        offset: null,
        limit: null,
      },
      sort: {
        value: SortOrderValues.CREATED_AT,
        order: SortOrderOptions.ASC,
      },
      queryString: '',
    },
    { enabled: enabled },
  );

  const { shares } = shareList;

  return (
    <>
      {item?.nodeId && <ShareListener nodeId={item.nodeId} />}
      <div className="flex h-full flex-col">
        <Options
          disabled={disabled || shareListIsPending}
          addButtonPosition="top"
          addButtonClassName="bg-ui-base!"
          containerClassName="bg-ui-panel"
          readOnly={readOnly}
          handleAddNewOption={() =>
            openModal(ModalId.EditCreateShare, {
              mode: 'create',
              nodeId: item?.nodeId,
              name: item?.name,
              description: item?.description,
              shareItem: item,
              firstShare: shares.length === 0,
              shareType: currentSelectedType,
              asset: asset,
            })
          }
          emptyState={
            <p className="p-4 text-center text-balance text-icon-base">
              {tShares('emptyForType', { type: currentSelectedType })}
              {!readOnly && (
                <>
                  <br />{' '}
                  {tShares.rich('addShareHint', {
                    type: currentSelectedType,
                    plusIcon: () => (
                      <>
                        {`"`}
                        <Plus className="inline icon-4" />
                        {`"`}
                      </>
                    ),
                  })}
                </>
              )}
            </p>
          }
          fullHeight
        >
          {shares.map((share) => (
            <ShareOptionItem
              key={share.id}
              option={share}
              onClick={() => {
                if (readOnly) {
                  return;
                }
                openModal(ModalId.EditCreateShare, {
                  mode: 'edit',
                  shareId: share.id,
                  nodeId: item.nodeId,
                  shareType: currentSelectedType,
                  asset: asset,
                  shareItem: item,
                });
              }}
              lastShare={shares.length === 1}
              readOnly={readOnly}
            />
          ))}
        </Options>
      </div>
    </>
  );
};
